"use client";

import { ComponentPropsWithRef, KeyboardEvent } from "react";

type Props = Omit<ComponentPropsWithRef<"div">, "onClick" | "onKeyDown" | "role" | "tabIndex"> & {
    onActivate: () => void;
};

/**
 * A div that behaves like a button, for the cases where a real <button> is not an
 * option: an interactive descendant inside it, or a whole card whose accessible name
 * would otherwise swallow every line of its content.
 *
 * The three parts of keyboard access — role, tab stop and Enter/Space — are applied
 * together and cannot be passed in, because supplying only some of them is the exact
 * defect this replaces.
 */
export const Pressable = ({ onActivate, ...props }: Props) => {
    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (event.key !== "Enter" && event.key !== " ") return;

        event.preventDefault();
        onActivate();
    };

    return (
        <div
            {...props}
            onClick={onActivate}
            onKeyDown={handleKeyDown}
            role="button"
            tabIndex={0}
        />
    );
};

export default Pressable;
